import { AlertV2, ButtonV2, ModalV2, StandardAlert } from '@cimplico/components';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import Currency from '../../components/currency/Currency';
import { TrialBalanceUploadErrors } from './TrialBalanceUpload';

type TrialBalanceErrorAlertProps = {
    errors: TrialBalanceUploadErrors;
};

/**
 * Converts a camel case string to normal case.
 *
 * e.g. `reportCode` -> `report Code
 * @param str Camel case string to convert
 * @returns Normal cased string.
 */
function camelToNormalCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function TrialBalanceErrorAlert({ errors }: TrialBalanceErrorAlertProps): JSX.Element {
    const [viewErrorDetails, setViewErrorDetails] = useState(false);
    return (
        <>
            {viewErrorDetails && (
                <ModalV2 isOpen onClose={(): void => setViewErrorDetails(false)}>
                    <ModalV2.Header>
                        <div className="flex gap-2 items-start">
                            <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                                <ExclamationCircleIcon className="text-red-500 h-6 w-6" />
                            </div>
                            <div className="text-left flex flex-col gap-y-0.5">
                                <p className="text-gray-800 font-medium">Invalid Format</p>
                                <p className="text-sm text-gray-500">The file provided is invalid and does not meet requirements.</p>
                            </div>
                            <button onClick={(): void => setViewErrorDetails(false)} className="outline-none">
                                <XMarkIcon className="text-gray-500 h-5 w-5" />
                            </button>
                        </div>
                    </ModalV2.Header>
                    <ModalV2.Body>
                        <AlertV2
                            description="Please address the following errors before re-uploading the CSV file."
                            type="error"
                            showIcon={false}
                            size="sm"
                        ></AlertV2>
                        {/* Balance */}
                        {!errors.balance.isBalanced && (
                            <>
                                <div className="text-left mt-2 mb-2 space-y-2">
                                    {errors.balance.currentAmount !== 0 && (
                                        <div>
                                            <b className="text-xs text-gray-600">Current amount is out by:</b>
                                            <Currency
                                                className="text-xs  text-red-600 font-bold"
                                                value={Math.abs(errors.balance.currentAmount)}
                                                withParentheses={errors.balance.currentAmount > 0}
                                            />
                                        </div>
                                    )}
                                    {errors.balance.priorAmount !== 0 && (
                                        <div>
                                            <b className="text-xs text-gray-600">Prior amount is out by:</b>
                                            <Currency
                                                className="text-xs  text-red-600 font-bold"
                                                value={Math.abs(errors.balance.priorAmount)}
                                                withParentheses={errors.balance.priorAmount > 0}
                                            />
                                        </div>
                                    )}
                                    {errors.issues.length > 0 && <hr />}
                                </div>
                            </>
                        )}
                        <div className="text-left mt-2 space-y-4 max-h-[400px] overflow-scroll">
                            {errors.issues.map((err) => (
                                <div key={err.line}>
                                    <p className="text-xs text-gray-500 mb-1">Row {err.line}:</p>
                                    <ul className="list-inside text-xs text-gray-600 pl-4 space-y-2">
                                        {err.errors.map((error, j) => (
                                            <li key={j} className="">
                                                <b className="capitalize shrink-0">{camelToNormalCase(error.field).toLowerCase()}:&nbsp;</b>
                                                <p>{error.message}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </ModalV2.Body>
                    <ModalV2.Footer>
                        <div className="flex justify-end">
                            <ButtonV2
                                type="secondary"
                                label="Close & try again"
                                size="sm"
                                onClick={(): void => setViewErrorDetails(false)}
                            />
                        </div>
                    </ModalV2.Footer>
                </ModalV2>
            )}
            <AlertV2 type="error" title="Unable to import trial balance." description="Click to view more details." className="mb-2">
                <AlertV2.Action
                    label="View details"
                    onClick={(): void => {
                        setViewErrorDetails(true);
                    }}
                />
            </AlertV2>
        </>
    );
}

export default TrialBalanceErrorAlert;
